<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useMeta } from "/src/composables/use-meta";
import axios from 'axios';
import { BASE_URL, BASE_URL_2 } from '/src/Config/config';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';

useMeta({ title: "Driver Violation Details" });

const store = useStore();

const route = useRoute();
const router = useRouter();
const isLoading = ref(false);
const error = ref(null);
const driver = ref({
    driver: 'Loading...',
    employee_id: ''
});
const violationActions = ref([]);
const trucks = ref([]);
const trailers = ref([]);
const loading_order_item = ref([]);

// Search and filters
const dateRange = ref({
    start: null,
    end: null
});

const showWarningModal = ref(false);
const warningReason = ref('');
const violationCount = ref(0);
const requiresWarning = ref(false);

// Get username from store
const username = computed(() => {
    const user = store.state.user;
    return user?.username || user?.name || 'User';
});

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showInfoAlert = (message) => {
    Swal.fire({
        icon: 'info',
        title: 'Info',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showWarningAlert = (title, message) => {
    return Swal.fire({
        icon: 'warning',
        title: title,
        text: message,
        padding: '2em',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, proceed',
        cancelButtonText: 'Cancel'
    });
};

const employeeId = route.params.employeeId;

// Initialize driver with basic info
driver.value = {
    driver: 'Loading driver...',
    employee_id: employeeId
};

// Get driver details and violations
const checkViolationCount = async () => {
    if (!dateRange.value.start || !dateRange.value.end) {
        showErrorAlert('Please select a date range');
        return;
    }

    try {
        isLoading.value = true;

        const response = await axios.get(`${BASE_URL}/violation-actions/employee/check-warning/${employeeId}`, {
            params: {
                start_date: dateRange.value.start,
                end_date: dateRange.value.end
            },
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        violationCount.value = response.data.count;
        requiresWarning.value = response.data.requires_warning;

        if (violationCount.value > 0) {
            showWarningAlert(
                'Violations Found',
                `Found ${violationCount.value} violations in selected period. ${requiresWarning.value ? 'This qualifies for a warning letter.' : ''}`
            );
        } else {
            showInfoAlert('No qualifying violations found in the selected period');
        }
    } catch (err) {
        showErrorAlert(err.response?.data?.message || 'Failed to check violations');
    } finally {
        isLoading.value = false;
    }
};

const issueWarningLetter = async () => {
    if (!warningReason.value) {
        showErrorAlert('Please enter a reason for the warning');
        return;
    }

    const result = await showWarningAlert(
        'Confirm Warning Letter',
        'Are you sure you want to issue a warning letter for these violations?'
    );

    if (!result.isConfirmed) return;

    try {
        isLoading.value = true;

        await axios.post(`${BASE_URL}/violation-actions/employee/issue-warning/${employeeId}`, {
            start_date: dateRange.value.start,
            end_date: dateRange.value.end,
            reason: warningReason.value
        }, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        showSuccessAlert('Warning letter issued successfully!');
        showWarningModal.value = false;
        warningReason.value = '';
        router.push('/violation/action/warning-letters/list');
    } catch (err) {
        showErrorAlert(err.response?.data?.message || 'Failed to issue warning');
    } finally {
        isLoading.value = false;
    }
};

const loadInitialData = async () => {
    isLoading.value = true;
    error.value = null;

    try {
        if (!employeeId) {
            throw new Error('No employee ID provided');
        }

        // Fetch all data in parallel
        const [driverRes, actionsRes, trucksRes, trailersRes, loadingOrdersRes] = await Promise.all([
            axios.get(`${BASE_URL_2}/employee/driver/show`),
            axios.get(`${BASE_URL}/violation-actions/employee/${employeeId}`, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            }),
            axios.get(`${BASE_URL_2}/vehicle/truck/show`),
            axios.get(`${BASE_URL_2}/vehicle/trailer/show`),
            axios.get(`${BASE_URL_2}/loading-order-item/show`)
        ]);

        // Process driver data
        const allDrivers = driverRes.data.drivers || driverRes.data.data || driverRes.data || [];
        const foundDriver = allDrivers.find(d => d.employee_id == employeeId);

        if (!foundDriver) {
            throw new Error('Driver not found');
        }

        driver.value = foundDriver;

        // Process other data
        violationActions.value = actionsRes.data.data || [];
        trucks.value = trucksRes.data.trucks || trucksRes.data.data || trucksRes.data || [];
        trailers.value = trailersRes.data.trailers || trailersRes.data.data || trailersRes.data || [];
        loading_order_item.value = loadingOrdersRes.data.loading_order_items || loadingOrdersRes.data.data || loadingOrdersRes.data || [];

    } catch (err) {
        console.error("Error loading driver violation details:", err);
        showErrorAlert(err.message || 'Failed to load driver details');

        // Set fallback driver info
        driver.value = {
            driver: 'Unknown Driver',
            employee_id: route.params.employeeId || 'N/A'
        };
    } finally {
        isLoading.value = false;
    }
}

onMounted(async () => {
    await loadInitialData();
});

// Helper functions
const getTruckNumber = (truckId) => {
    const truck = trucks.value.find(t => t.id == truckId);
    return truck?.registration_number || 'N/A';
};

const getTrailerNumber = (trailerId) => {
    const trailer = trailers.value.find(t => t.id == trailerId);
    return trailer?.registration_number || 'N/A';
};

const getCargoType = (loadingOrderItemId) => {
    const item = loading_order_item.value.find(i => i.id == loadingOrderItemId);
    return item?.cargo_type || 'N/A';
};

const getDirection = (loadingOrderItemId) => {
    const item = loading_order_item.value.find(i => i.id == loadingOrderItemId);
    return item?.type || 'N/A';
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
};

// Clear filters and reload data
const clearFilters = () => {
    dateRange.value.start = null;
    dateRange.value.end = null;
    violationCount.value = 0;
    requiresWarning.value = false;
    showWarningModal.value = false;
    warningReason.value = '';
    error.value = null;
    loadInitialData();
};

const goBack = () => {
    router.push('/violation/reports/drivers');
    clearFilters();
};
</script>

<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <router-link to="/violation/reports/drivers">Violation Reports</router-link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>Driver Violation Details</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <h3 class="mb-0">Violation Details for: <strong>{{ driver.driver }}</strong></h3>
                            <button class="btn btn-secondary" @click="goBack">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="feather feather-arrow-left">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12 19 5 12 12 5"></polyline>
                                </svg>
                                Back
                            </button>
                        </div>

                        <div class="row mb-4">
                            <div class="col-md-3">
                                <label class="form-label">Start Date</label>
                                <input type="date" class="form-control" v-model="dateRange.start">
                            </div>
                            <div class="col-md-3">
                                <label class="form-label">End Date</label>
                                <input type="date" class="form-control" v-model="dateRange.end">
                            </div>
                            <div class="col-md-3 d-flex align-items-end mt-2">
                                <button class="btn btn-info" @click="checkViolationCount">
                                    Check Violations
                                </button>
                            </div>
                            <div class="col-md-3 d-flex align-items-end mt-2">
                                <button class="btn btn-danger" @click="clearFilters">
                                    Clear Filters
                                </button>
                            </div>
                        </div>

                        <!-- Show violation count and warning button -->
                        <div v-if="violationCount > 0" class="alert alert-warning mb-4">
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    Found {{ violationCount }} Over Speeding and Night Driving violations in selected
                                    period.
                                    <span v-if="requiresWarning" class="fw-bold">This qualifies for a warning
                                        letter.</span>
                                </div>
                                <button v-if="requiresWarning" class="btn btn-danger" @click="showWarningModal = true">
                                    Issue Warning Letter
                                </button>
                            </div>
                        </div>

                        <!-- Warning Letter Modal -->
                        <div v-if="showWarningModal" class="modal fade show"
                            style="display: block; background: rgba(0,0,0,0.5)">
                            <div class="modal-dialog">
                                <div class="modal-content">
                                    <div class="modal-header">
                                        <h5 class="modal-title">Issue Warning Letter</h5>
                                        <button type="button" class="btn-close"
                                            @click="showWarningModal = false"></button>
                                    </div>
                                    <div class="modal-body">
                                        <div class="mb-3">
                                            <label class="form-label">Reason for Warning</label>
                                            <textarea class="form-control" v-model="warningReason" rows="3"></textarea>
                                        </div>
                                        <div class="alert alert-info">
                                            This will update all selected violations to "Warning Letter" status.
                                        </div>
                                    </div>
                                    <div class="modal-footer">
                                        <button type="button" class="btn btn-secondary"
                                            @click="showWarningModal = false">
                                            Cancel
                                        </button>
                                        <button type="button" class="btn btn-danger" @click="issueWarningLetter">
                                            Confirm Warning Letter
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Error message -->
                        <div v-if="error" class="alert alert-danger">
                            {{ error }}
                        </div>

                        <!-- Loading state -->
                        <div v-else-if="isLoading" class="text-center p-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-2">Loading driver details...</p>
                        </div>

                        <!-- No violations found -->
                        <div v-else-if="violationActions.length === 0" class="alert alert-info">
                            No violation actions found for this driver.
                        </div>

                        <!-- Violations table -->
                        <div v-else class="table-responsive">
                            <table class="table table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Date/Time</th>
                                        <th>Violation</th>
                                        <th>Truck</th>
                                        <th>Trailer</th>
                                        <th>Cargo Type</th>
                                        <th>Direction</th>
                                        <th>Action Taken</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(action, index) in violationActions" :key="action.id">
                                        <td>{{ index + 1 }}</td>
                                        <td>{{ formatDate(action.created_at) }}</td>
                                        <td>{{ action.violation?.title || 'N/A' }}</td>
                                        <td>{{ getTruckNumber(action.truck_id) }}</td>
                                        <td>{{ getTrailerNumber(action.trailer_id) }}</td>
                                        <td>{{ getCargoType(action.loading_order_item_id) }}</td>
                                        <td>{{ getDirection(action.loading_order_item_id) }}</td>
                                        <td>{{ action.taken_action?.name || 'N/A' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.table-responsive {
    overflow-x: auto;
}

.table th,
.table td {
    vertical-align: middle;
}

.alert {
    margin-bottom: 0;
}

/* SweetAlert2 customizations */
.swal2-popup {
    font-size: 1.6rem !important;
}
</style>